import { memo, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { conditions } from "../../../../../helpers";
import { ConditionName, IChatMessage } from "../../../../../interfaces";
import { fetchChatMessage, handleConnectSocket } from "../../../../../redux/actions/chat.actions";
import { setChatPopover } from "../../../../../redux/slices/popoversSlice";
import { setMessages, setShowChat, setMessageCount } from "../../../../../redux/slices/chatSlice";
import { shallowEqual } from "react-redux";
import dynamic from "next/dynamic";
import ChatObserver from "./chatObserver";
import PinMessage from "./pinMessage";

const ChatIcon = dynamic(() => import("../../icon-components/chatIcon"));
const Popover = dynamic(() => import("../popover/popover"));
const ChatCard = dynamic(() => import("./chatCard"));
const AddChatComment = dynamic(() => import("./addChatComment"));

const Chat = () => {
    const dispatch = useAppDispatch();
    const { chatPopover } = useAppSelector(({ popovers: { chatPopover } }) => ({ chatPopover }), shallowEqual);
    const { messages, showChat, socket } = useAppSelector(
        ({ chats: { messages, showChat, socket, pinMessage } }) => ({ messages, showChat, socket, pinMessage }),
        shallowEqual
    );
    useAppSelector(({ videoData: { isComplexDesktop, isComplexPortrait } }) => ({ isComplexDesktop, isComplexPortrait }), shallowEqual);

    const [scrollEnd, setScrollEnd] = useState<boolean>(false);

    useEffect(() => {
        if (conditions(ConditionName.showChatPopover) || conditions(ConditionName.isComplexDesktop)) handleConnectSocket(setScrollEnd);
        return () => socket && socket.disconnect();
    }, [chatPopover]);

    useEffect(() => {
        if (scrollEnd && messages && messages.length) fetchChatMessage(messages.length, setScrollEnd);
    }, [scrollEnd]);

    const chatSkeleton = (skeletonNumber: number) => {
        return (
            <>
                {Array.from(Array(skeletonNumber).keys()).map((item) => {
                    return (
                        <div
                            key={item}
                            role="status"
                            className="pl-mb-1 pl-p-4 pl-bg-gray-200  pl-animate-pulse dark:pl-bg-gray-700 dark:pl-opacity-60 pl-space-y-8 md:pl-space-y-0 md:pl-space-x-8 rtl:pl-space-x-reverse md:pl-flex md:pl-items-center"
                        >
                            <div className="pl-w-[90%]">
                                <div className="pl-flex pl-items-center pl-gap-3 pl-mb-3">
                                    <div className="pl-h-12 pl-w-12 pl-bg-gray-100 pl-rounded-full dark:pl-bg-gray-500"></div>
                                    <div className="pl-flex pl-flex-col pl-gap-3 pl-w-1/2">
                                        <div className="pl-h-1.5 pl-bg-gray-100 pl-rounded-full dark:pl-bg-gray-500"></div>
                                        <div className="pl-h-1.5 pl-bg-gray-100 pl-rounded-full dark:pl-bg-gray-500 !pl-w-2/3"></div>
                                    </div>
                                </div>
                                <div className="pl-h-1.5 pl-bg-gray-100 pl-rounded-full dark:pl-bg-gray-500 pl-w-[250px] pl-mb-2"></div>
                                <div className="pl-h-1.5 pl-bg-gray-100 pl-rounded-full dark:pl-bg-gray-500 pl-w-[270px] pl-mb-2"></div>
                                <div className="pl-h-1.5 pl-bg-gray-100 pl-rounded-full dark:pl-bg-gray-500 pl-w-[210px]"></div>
                            </div>
                            <span className="pl-sr-only">Loading...</span>
                        </div>
                    );
                })}
            </>
        );
    };

    return (
        <>
            {conditions(ConditionName.isComplexPortrait) ? (
                <>
                    <div
                        className="pl-flex pl-items-center pl-gap-[14px] pl-rounded-[20px] pl-text-secondary-700 dark:pl-text-secondary-50 pl-bg-gray-100 dark:pl-bg-dark-3 pl-px-4 pl-py-[7px] pl-cursor-pointer"
                        onClick={() => dispatch(setChatPopover(true))}
                    >
                        <ChatIcon className="pl-w-5 pl-h-5 pl-fill-z-gray-500 dark:pl-fill-dark-5" />
                        <span className="pl-whitespace-nowrap">گفت‌وگوی زنده</span>
                    </div>
                    <Popover
                        type="modal"
                        status={conditions(ConditionName.showChatPopover)}
                        onClose={() => {
                            dispatch(setChatPopover(false));
                            dispatch(setShowChat(false));
                            dispatch(setMessages(undefined));
                            dispatch(setMessageCount(0));
                        }}
                        parentClassNames="!pl-h-auto"
                        isFocusOnInput={true}
                        isFullHeight={true}
                        isHasCatchUpLineOnTop={true}
                        isHeader={true}
                        headerTitle="گفت‌وگوی زنده"
                        headerClassName="!pl-mb-0"
                        pinContent={conditions(ConditionName.hasPinMessage) ? <PinMessage /> : ""}
                    >
                        <>
                            <div
                                className={`pl-flex pl-flex-col pl-gap-4 pl-px-4 pl-pt-4 pl-mb-[120px] ${
                                    !conditions(ConditionName.hasPinMessage) && "pl-mt-[65px]"
                                }`}
                            >
                                {messages && messages.map((message: IChatMessage) => <ChatCard key={message.id} message={message} />)}
                                {(messages === undefined || scrollEnd) && chatSkeleton(4)}
                                <ChatObserver setScrollEnd={setScrollEnd} />
                            </div>
                            <AddChatComment />
                        </>
                    </Popover>
                </>
            ) : (
                <>
                    {showChat ? (
                        <div className="pl-relative pl-border pl-border-z-gray-400 pl-rounded-[6px] pl-flex pl-flex-col pl-ml-4 pl-flex-1 pl-h-1">
                            <p className="pl-px-4 pl-text-[14px] pl-font-bold pl-pt-4 pl-border-b pl-border-b-z-gray-400 pl-pb-4">گفتگو زنده</p>
                            {conditions(ConditionName.hasPinMessage) && <PinMessage />}
                            <div className="pl-flex pl-flex-col pl-gap-4 pl-p-4 pl-flex-1 pl-h-full pl-overflow-auto">
                                {messages && messages.map((message: IChatMessage) => <ChatCard key={message.id} message={message} />)}
                                {(messages === undefined || scrollEnd) && chatSkeleton(4)}
                                <ChatObserver setScrollEnd={setScrollEnd} />
                            </div>
                            <AddChatComment />
                            <button
                                className="pl-bg-secondary-200 dark:pl-bg-dark-3 pl-border-t pl-border-t-z-gray-400 pl-py-[11px] pl-text-[14px] pl-font-bold"
                                onClick={() => dispatch(setShowChat(false))}
                            >
                                مخفی کردن گفتگوی زنده
                            </button>
                        </div>
                    ) : (
                        <button
                            className="pl-bg-secondary-200 dark:pl-bg-z-gray-500 pl-text-gray-800 dark:pl-text-white pl-text-[14px] pl-font-bold pl-rounded-[20px] pl-py-3 pl-ml-4 pl-mb-4"
                            onClick={() => dispatch(setShowChat(true))}
                        >
                            نمایش گفتگوی زنده
                        </button>
                    )}
                </>
            )}
        </>
    );
};

export default memo(Chat);
